import Vue from 'vue'
import gql from 'graphql-tag'
import { Component, Watch } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'
import { State } from 'vuex-class'

import { ApolloResponse } from '@/interfaces/apollo'
import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { Contact } from '@/modules/Contact/interfaces/contact'
import { showErrors } from '@/utils/snack'
import { tablePerPageOptions } from '@/utils/tablePerPageOptions'
import { dateTimeToDisplay } from '@/utils/dateTime'
import { formatPhoneNumber } from '@/utils/phoneNumberFormat'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppSearch from '@/components/AppSearch.vue'
import AppRefresh from '@/components/AppRefresh.vue'
import AppTextField from '@/components/AppTextField.vue'
import AppFormCheckbox from '@/components/AppFormCheckbox.vue'
import AppListSearchBtn from '@/components/AppListSearchBtn.vue'

import AccountDialog from '@/modules/Account/scripts/account-dialog'
import ContactDialog from '@/modules/Contact/scripts/contact-dialog'
import { getContacts, getAllGlobalSearchContacts } from '@/modules/Contact/gql/contact.gql'
import { getAccount } from '@/modules/Account/gql/account.gql'
import ListPage from '@/mixins/list-page'
import AppSimpleTextField from '@/components/AppSimpleTextField.vue'
import AppFilterButton from '@/components/AppFilterButton.vue'
import { PageState } from '../../../store/modules/page'

@Component({
  components: {
    AppBreadcrumbs,
    AppSearch,
    AppRefresh,
    AppTextField,
    AppFormCheckbox,
    AppListSearchBtn,
    AppSimpleTextField,
    AppFilterButton
  }
})
export default class ContactList extends mixins(AccountDialog, ContactDialog, ListPage) {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'Client Information'
    },
    {
      text: 'Contacts'
    }
  ]
  searchVal = ''

  contacts: Contact[] = []

  windowSize = {
    w: 0,
    h: 0,
    cW: 0
  }

  orderBy: {
    [key: string]: string
  } = {
    id: 'ID',
    name: 'NAME',
    phone: 'PHONE',
    email: 'EMAIL',
    updatedAt: 'UPDATED_AT'
  }

  dateTimeToDisplay = dateTimeToDisplay

  tableColumn = [
    { field: 'name', title: 'Contact Name', slots: { default: 'name_default' }, sortable: true, resizable: true },
    { field: 'accountName',
      title: 'Account Name',
      slots: { default: 'account_name_default' },
      resizable: true
    },
    { title: 'Title',
      field: 'title',
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', row.title ? row.title : '-')
        ]
      }
      },
      resizable: true
    },
    { title: 'Phone',
      field: 'phone',
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', row.phone ? formatPhoneNumber(row.phone, row.countryCode) : '-')
        ]
      }
      },
      resizable: true,
      sortable: true
    },
    { title: 'Ext',
      field: 'extensionNumber',
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', row.extensionNumber ? row.extensionNumber : '-')
        ]
      }
      },
      width: 80,
      resizable: true,
      sortable: false
    },
    { title: 'Email',
      field: 'email',
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', row.email ? row.email : '-')
        ]
      }
      },
      sortable: true
    },
    { field: 'actions', title: 'Actions', slots: { default: 'action_default' }, fixed: 'right', width: 74 }
  ]

  tableOptions = {
    loading: false,
    tablePage: tablePerPageOptions(),
    sortBy: 'id',
    sortDesc: true
  }

  tableLoading = false
  accountId = -1

  @State(state => state.page) page!: PageState

  get isShowFilter () {
    return this.$route.name !== 'ContactListWithSearchval'
  }

  created () {
    const { search, id } = this.$route.params
    this.searchVal = search
    if (!id) {
      this.restorePage(this.page[this.$route.name || ''])
    } else {
      this.accountId = Number(id)
    }
  }

  async mounted () {
    this.onResize()
    await this.restorePage(this.page[this.$route.name || ''])
    this.getList()
  }

  onResize () {
    const container = this.$refs.container as Vue & { clientWidth: number }
    this.windowSize = { w: window.innerWidth, h: window.innerHeight, cW: container.clientWidth }
  }

  routeTo (name: string, params: any) {
    this.$router.push({
      name,
      params
    })
  }

  routeToDetail (item: any) {
    return {
      name: 'ContactDetailDetail',
      params: {
        id: item.id
      }
    }
  }

  routeToAccountDetail (item: any) {
    if (item.accountName) {
      return {
        name: 'AccountDetailDetail',
        params: {
          id: item.accountName.id
        }
      }
    }
  }

  async deleteContactConfirm () {
    await this.deleteContactPost()
    this.getList()
  }

  async getAccount () {
    this.tableLoading = true
    const accountId = Number(this.$route.params.id)
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getAccount,
        variables: {
          accountId
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.contacts = result.data.getAccount.contacts
      this.tableLoading = false
      this.options.tablePage.total = this.contacts.length

      if (this.breadcrumbs.length === 1) {
        this.breadcrumbs.unshift({
          text: result.data.getAccount.name,
          to: {
            name: 'AccountDetailContact',
            params: {
              id: result.data.getAccount.id
            }
          }
        })
      }
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getContacts () {
    const { sortBy, sortDesc, tablePage } = this.options
    const { currentPage, pageSize } = tablePage
    const searchVal = this.searchVal ? this.searchVal : null
    const orderBy = this.orderBy[sortBy] || 'UPDATED_AT'
    const filter: {
      [key: string]: any;
    } = {}
    if (this.filter.contactName) {
      filter.name = this.filter.contactName
    }
    if (this.filter.accountName) {
      filter.accountName = this.filter.accountName
    }
    if (this.filter.phone) {
      filter.phone = this.filter.phone
    }
    if (this.filter.accountId && this.filter.accountId !== -1) {
      filter.accountId = this.filter.accountId
    }
    this.tableLoading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getContacts,
        variables: {
          filter,
          order: {
            orderBy: orderBy,
            asc: !sortDesc
          },
          paging: {
            offset: (currentPage - 1) * pageSize,
            limit: pageSize
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.contacts = result.data.getContacts.contacts
      this.tableLoading = false
      this.options.tablePage.total = result.data.getContacts.paging.totalCount
      this.storePage()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getContactsFromDashboard () {
    const { sortBy, sortDesc, tablePage } = this.options
    const { currentPage, pageSize } = tablePage
    const orderBy = this.orderBy[sortBy] || 'UPDATED_AT'
    this.tableLoading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getAllGlobalSearchContacts,
        variables: {
          query: this.$route.params.search,
          order: {
            orderBy: orderBy,
            asc: !sortDesc
          },
          paging: {
            offset: (currentPage - 1) * pageSize,
            limit: pageSize
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.contacts = result.data.globalSearchContacts.contacts
      this.tableLoading = false
      this.options.tablePage.total = result.data.globalSearchContacts.paging.totalCount
      this.storePage()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async refreshList () {
    await this.clearPage()
    await this.restorePage(this.page[this.$route.name || ''])
    this.getList()
  }

  onCreateSuccess () {
    this.hideContactEditDialog()
    this.getList()
  }

  sortTable (column: any) {
    this.options.sortBy = column.property
    this.options.sortDesc = column.order === 'desc'
    this.getList()
  }

  handlePageChange (pageData: any) {
    this.options.tablePage.currentPage = pageData.currentPage
    this.options.tablePage.pageSize = pageData.pageSize
    this.getList()
  }

  getList () {
    if (this.$route.name === 'ContactListWithSearchval') {
      this.getContactsFromDashboard()
    } else {
      if (this.$route.params.id) {
        this.filter.accountId = Number(this.$route.params.id)
      } else {
        this.filter.accountId = -1
      }
      this.getContacts()
    }
  }
}
